import {RouteReuseStrategy, DetachedRouteHandle, ActivatedRouteSnapshot} from '@angular/router';

interface RouteHandleMeta {
  routeUrl: string;
  routeConfigPath: string;
  routeConfig: any;
  selector: string;
}

export class SimpleReuseStrategy implements RouteReuseStrategy {
  public static handlers: { [key: string]: DetachedRouteHandle } = {};

  private static handlerMetas: { [key: string]: RouteHandleMeta } = {};
  private static waitDelete: string;

  /**
   * 如果没有快照且删除的是当前页面，则直接关闭不进行缓存
   */
  public static deleteRouteSnapshot(path: string, selector: string): void {
    SimpleReuseStrategy.destroyHandler(path.replace(/\//g, '_') + '_' + selector);
  }

  public static deleteCurrentRouteSnapshot(route: ActivatedRouteSnapshot): void {
    const name = SimpleReuseStrategy.getPath(route);
    delete SimpleReuseStrategy.handlers[name];
    delete SimpleReuseStrategy.handlerMetas[name];
    SimpleReuseStrategy.waitDelete = name;
  }

  public static deleteDetachedRouteSnapshot(path: string, selector: string): void {
    SimpleReuseStrategy.destroyHandler(path.replace(/\//g, '_') + '_' + selector);
  }

  /**
   * 判断当前路由是否需要缓存
   * 这个方法返回false时则路由发生变化并且其余方法会被调用
   * @param {ActivatedRouteSnapshot} future
   * @param {ActivatedRouteSnapshot} curr
   * @returns {boolean}
   * @memberof CacheRouteReuseStrategy
   */
  public shouldReuseRoute(future: ActivatedRouteSnapshot, curr: ActivatedRouteSnapshot): boolean {
    return future.routeConfig === curr.routeConfig
      && JSON.stringify(future.params) === JSON.stringify(curr.params);
  }

  /**
   * 当离开当前路由时这个方法会被调用
   * 如果返回 true 则 store 方法会被调用
   * @param {ActivatedRouteSnapshot} route
   * @returns {boolean}
   * @memberof CacheRouteReuseStrategy
   */
  public shouldDetach(route: ActivatedRouteSnapshot): boolean {
    if (!route.routeConfig || route.routeConfig.loadChildren) {
      return false;
    }
    return true;
  }

  /**
   * 将路由写入缓存
   * 在这里具体实现如何缓存 RouteHandle
   * 提供了我们离开的路由和 RouteHandle
   * @param {ActivatedRouteSnapshot} route
   * @param {DetachedRouteHandle} detachedTree
   * @memberof CacheRouteReuseStrategy
   */
  public store(route: ActivatedRouteSnapshot, detachedTree: DetachedRouteHandle): void {
    const routeKey = SimpleReuseStrategy.getPath(route);
    if (SimpleReuseStrategy.waitDelete && SimpleReuseStrategy.waitDelete === routeKey) {
      // 如果待删除是当前路由则不存储快照
      detachedTree['componentRef'].destroy();
      SimpleReuseStrategy.waitDelete = null;
      delete SimpleReuseStrategy.handlerMetas[routeKey];

      return;

    }
    if (routeKey === '_login_app-login') { // 如果时登录页面则不缓存，防止退出登录时因为组件被销毁报错
      return;
    }
    if (routeKey === '_basic_form_basic_form_app-basic-form') {
      return;
    }
    // if (SimpleReuseStrategy.getPath(route) === '_meter_read_meter_read_import_app-meter-reading-import') {
    //   return;
    // }
    SimpleReuseStrategy.handlers[routeKey] = detachedTree;
    SimpleReuseStrategy.handlerMetas[routeKey] = SimpleReuseStrategy.getRouteMeta(route);
  }

  /**
   * 路由被导航 如果此方法返回 true 则触发 retrieve 方法
   * 如果返回 false 这个组件将会被重新创建
   * @param {ActivatedRouteSnapshot} route
   * @returns {boolean}
   * @memberof CacheRouteReuseStrategy
   */
  public shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const routeKey = SimpleReuseStrategy.getPath(route);
    if (!SimpleReuseStrategy.handlers[routeKey]) {
      return false;
    }
    if (!SimpleReuseStrategy.isHandlerCompatible(routeKey, route)) {
      SimpleReuseStrategy.destroyHandler(routeKey);
      return false;
    }
    return true;
  }

  /**
   * 从缓存读取cached route
   * 提供当前路由的参数（刚打开的路由），并且返回一个缓存的 RouteHandle
   * 可以使用这个方法手动获取任何已被缓存的 RouteHandle
   * @param {ActivatedRouteSnapshot} route
   * @returns {(DetachedRouteHandle | null)}
   * @memberof CacheRouteReuseStrategy
   */
  public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const routeKey = SimpleReuseStrategy.getPath(route);
    if (!SimpleReuseStrategy.handlers[routeKey]) {
      return null;
    }
    if (!SimpleReuseStrategy.isHandlerCompatible(routeKey, route)) {
      SimpleReuseStrategy.destroyHandler(routeKey);
      return null;
    }
    return SimpleReuseStrategy.handlers[routeKey];
  }

  private static destroyHandler(name: string): boolean {
    if (SimpleReuseStrategy.handlers[name]) {
      const ref = SimpleReuseStrategy.handlers[name];
      if (ref['componentRef'] && ref['componentRef'].destroy) {
        ref['componentRef'].destroy();
      }
      delete SimpleReuseStrategy.handlers[name];
      delete SimpleReuseStrategy.handlerMetas[name];
      return true;
    }
    delete SimpleReuseStrategy.handlerMetas[name];
    return false;
  }

  private static isHandlerCompatible(name: string, route: ActivatedRouteSnapshot): boolean {
    const storedMeta = SimpleReuseStrategy.handlerMetas[name];
    const currentMeta = SimpleReuseStrategy.getRouteMeta(route);
    return !!storedMeta
      && storedMeta.routeConfig === route.routeConfig
      && storedMeta.routeUrl === currentMeta.routeUrl
      && storedMeta.routeConfigPath === currentMeta.routeConfigPath
      && storedMeta.selector === currentMeta.selector;
  }

  private static getRouteMeta(route: ActivatedRouteSnapshot): RouteHandleMeta {
    return {
      routeUrl: route['_routerState'].url,
      routeConfigPath: route.routeConfig && route.routeConfig.path || '',
      routeConfig: route.routeConfig,
      selector: SimpleReuseStrategy.getSelector(route)
    };
  }

  private static getSelector(route: ActivatedRouteSnapshot): string {
    let selectors = '';
    if (route.routeConfig?.component) {
      for (const r in route.routeConfig.component) {
        if (r === 'ɵcmp') {
          if (route.routeConfig.component[r].selectors) {
            selectors = route.routeConfig.component[r].selectors[0].join('');
          }
        }
      }
    }
    return selectors;
  }

  private static getPath(route: ActivatedRouteSnapshot): string {
    // tslint:disable-next-line: no-string-literal
    // const path = route['_routerState'].url.replace(/\//g, '_');
    return route['_routerState'].url.replace(/\//g, '_')
      + '_' + SimpleReuseStrategy.getSelector(route);
    // return path;
  }
}
